import EntityType from './entityType';

import { InvoiceGroup } from '../components/Orders/utils';

import flatMap from 'lodash/flatMap';
import isSameMonth from 'date-fns/isSameMonth';
import { InvoiceStatus } from 'foundshared/src/api/invoices/constants';
import {
  formatDistanceToNowStrict,
  isAfter,
  isBefore,
  isSameDay,
  isTomorrow,
  startOfDay,
  startOfMonth,
} from 'date-fns';

import type { InvoiceSummary } from 'foundshared';
import type { OrderInvoiceSummaryResponse } from 'foundshared/src/api/orders/types';
import type { Palette } from '@mui/material/styles';
import type { EntityState } from '../store/entities/types';
import type { Order } from '../store/orders/types';

/**
 * When an order has an unpaid invoice and the payment due date is greater than or equal to today,
 * @param invoiceSummary: OrderInvoiceSummary
 */
export function isPaymentDue(invoiceSummary: OrderInvoiceSummaryResponse | InvoiceSummary) {
  if (!invoiceSummary) return false;

  const today = new Date();
  const paymentDue = new Date(invoiceSummary.dueDate);

  if (isSameDay(paymentDue, today)) {
    return today.getHours() < 15 && invoiceSummary.amountPaid !== invoiceSummary.total;
  }

  return isAfter(paymentDue, Date.now()) && invoiceSummary.amountPaid !== invoiceSummary.total;
}

export function getInvoices(orders: Order[], invoicesEntities: EntityState['invoiceSummaries']) {
  return flatMap(orders, (order) => order[EntityType.INVOICES].map((id) => invoicesEntities[id]));
}

export function getOrderTotalDue(orders: Order[], invoicesEntities: EntityState['invoiceSummaries']): number {
  const totals = orders.flatMap((order) => {
    const invoiceId = order[EntityType.INVOICES][0];
    if (invoiceId) {
      return invoicesEntities[invoiceId].amountDue;
    }
    return order.total;
  });
  return totals.reduce((totalA, totalB) => totalA + totalB, 0);
}

/**
 * Whent the order has an unpaid invoice and the payment due date in the past
 * @param invoiceSummary: OrderInvoiceSummary
 */
export function isPaymentOverdue(invoiceSummary: OrderInvoiceSummaryResponse | InvoiceSummary) {
  if (!invoiceSummary) return false;

  const today = startOfDay(Date.now());
  const paymentDue = new Date(invoiceSummary.dueDate);

  return (
    (isSameDay(paymentDue, new Date()) || isBefore(paymentDue, today)) &&
    invoiceSummary.amountPaid !== invoiceSummary.total
  );
}

/**
 * When the user has marked the invoice as paid.
 * @param invoiceSummary: OrderInvoiceSummary
 */
export function isPending(invoiceSummary: OrderInvoiceSummaryResponse | InvoiceSummary) {
  return !!invoiceSummary?.userMarkedAsPaidAt;
}

/**
 * Whent the invoiceSummary has PAID status and the amountPaid equals the total
 * @param invoiceSummary: Invoice summary
 */
export function isPaid(invoiceSummary: OrderInvoiceSummaryResponse | InvoiceSummary) {
  if (!invoiceSummary) return false;
  return (
    invoiceSummary && invoiceSummary.status === InvoiceStatus.PAID && invoiceSummary.amountPaid === invoiceSummary.total
  );
}

/**
 * Is the invoice due this month before the 30th (or last day of the month).
 * @param invoiceSummary: OrderInvoiceSummary
 */
export function isDueEndOfMonth(order: Order, invoice: OrderInvoiceSummaryResponse | InvoiceSummary) {
  // Pending orders
  if (!invoice) return isBefore(new Date(order.deliverAt), startOfMonth(new Date()));

  return isSameMonth(new Date(), new Date(invoice.dueDate));

  // Is it due on the 31st?
  // const dueDate = getDate(lastDayOfThisMonth) > 30 ? setDate(now, 30) : lastDayOfThisMonth;
  // return isBefore(new Date(invoiceSummary.dueDate), dueDate);
}

export const orderInvoices = (order: Order, invoicesEntities: EntityState['invoiceSummaries']) =>
  Object.values(order[EntityType.INVOICES]).map((invoiceId) => invoicesEntities[invoiceId]);

export const orderCost = (order: Order, invoicesEntities: EntityState['invoiceSummaries']) => {
  const invoice = orderInvoices(order, invoicesEntities)[0];
  return invoice?.amountDue ?? order.total;
};

/**
 * Transform the invoice status to frontend rules
 * @param invoiceSummary: OrderInvoiceSummary
 */
export const getInvoiceState = (orderInvoiceSummary: OrderInvoiceSummaryResponse | InvoiceSummary): InvoiceStatus => {
  if (isPaymentDue(orderInvoiceSummary)) {
    return InvoiceStatus.DUE;
  }
  if (isPaymentOverdue(orderInvoiceSummary)) {
    return InvoiceStatus.OVERDUE;
  }
  if (isPaid(orderInvoiceSummary)) {
    return InvoiceStatus.PAID;
  }
  return InvoiceStatus.UNKNOWN;
};

/**
 * Provide the various groups based on the state of an invoice
 * @param invoiceSummary: OrderInvoiceSummary
 */
export const getInvoiceGroups = (order: Order, invoiceEntities: EntityState['invoiceSummaries']): InvoiceGroup[] => {
  const invoiceGroups: InvoiceGroup[] = [];
  const invoices = orderInvoices(order, invoiceEntities);
  const invoice = invoices[0];

  if (isPending(invoice)) {
    return [InvoiceGroup.PAYMENT_PENDING, InvoiceGroup.PAID];
  }

  if (isPaid(invoice)) {
    return [InvoiceGroup.PAID];
  }

  if (isPaymentOverdue(invoice)) {
    return [InvoiceGroup.OVERDUE];
  }

  // Used in pre pay
  if (isPaymentDue(invoice) || !invoice) {
    invoiceGroups.push(InvoiceGroup.DUE);
  }

  // Used in post pay
  if (isDueEndOfMonth(order, invoice)) {
    invoiceGroups.push(InvoiceGroup.DUE_BEFORE_END_OF_MONTH);
  } else {
    invoiceGroups.push(InvoiceGroup.DUE_AFTER_END_OF_MONTH);
  }

  return invoiceGroups;
};

/**
 * Return a "due in" human string for a supplied date
 * @param invoiceSummary: OrderInvoiceSummary
 */
export const getDistanceToNow = (dueIn: string, addSuffix: boolean): string => {
  const dueDate = new Date(dueIn);
  if (isSameDay(dueDate, Date.now())) return 'today';
  if (isTomorrow(dueDate)) return 'tomorrow';

  return formatDistanceToNowStrict(dueDate, { addSuffix, unit: 'day' });
};

export const invoiceStatesProps = ({ secondary, accent, error, primary }: Palette, isTheSameDate: boolean) => ({
  [InvoiceStatus.PAID]: {
    label: 'Paid',
    backgroundColor: secondary.lighter,
    color: primary.main,
  },
  [InvoiceStatus.DUE]: {
    label: 'Due',
    backgroundColor: isTheSameDate ? error.main : accent.main,
    color: isTheSameDate ? error.contrastText : accent.contrastText,
  },
  [InvoiceStatus.SUBMITTED]: {
    label: 'Submitted',
    backgroundColor: secondary.lighter,
    color: secondary.contrastText,
  },
  [InvoiceStatus.AUTHORISED]: {
    label: 'Authorised',
    backgroundColor: secondary.lighter,
    color: secondary.contrastText,
  },
  [InvoiceStatus.DELETED]: {
    label: 'Deleted',
    backgroundColor: secondary.lighter,
    color: secondary.contrastText,
  },
  [InvoiceStatus.DRAFT]: {
    label: 'Draft',
    backgroundColor: secondary.lighter,
    color: secondary.contrastText,
  },
  [InvoiceStatus.VOIDED]: {
    label: 'Voided',
    backgroundColor: secondary.lighter,
    color: secondary.contrastText,
  },
  [InvoiceStatus.OVERDUE]: {
    label: 'Overdue',
    backgroundColor: error.main,
    color: error.contrastText,
  },
  [InvoiceStatus.UNKNOWN]: {
    label: '',
    backgroundColor: secondary.lighter,
    color: secondary.contrastText,
  },
});

/**
 * Sort orders via the invoice due date
 * @param orders: Orders
 * @param invoices: InvoicesEntities
 */
export const sortOrdersByInvoiceDueDate = (orders: Order[], invoices: EntityState['invoiceSummaries']): Order[] => {
  const sorted = orders.sort((orderA, orderB) => {
    const invoiceA = orderInvoices(orderA, invoices)[0];
    const invoiceB = orderInvoices(orderB, invoices)[0];
    if (!invoiceA || !invoiceB) return -1;
    return isAfter(new Date(invoiceA.dueDate), new Date(invoiceB.dueDate)) ? 1 : -1;
  });
  return sorted;
};

/**
 * Sort orders via order deliver date.
 * @param orders: Orders
 */
export const sortOrdersByDeliverDate = (orders: Order[]): Order[] =>
  orders.sort((orderA, orderB) => (isAfter(new Date(orderA.deliverAt), new Date(orderB.deliverAt)) ? 1 : -1));
